/**
 * Client for the backend maps API (`/api/property/geo/*`): place search,
 * geocoding, nearby places and Google map tiles. The Google key lives only on
 * the server; the browser never talks to Google or OpenStreetMap directly.
 *
 * Search, geocoding and nearby calls carry a short-lived "geo pass"
 * (`x-geo-pass`). Signed-in users get one with their session token. Signed-out
 * visitors (public Listings, the "Request map" pop-up) get one after the same
 * invisible reCAPTCHA the login and contact forms use, fetched on their first
 * search, so the paid endpoints can't be scripted by anyone who finds the URL.
 * Map tiles need no pass (see googleTileUrl).
 */
import axios from "axios";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import ReCAPTCHA from "react-google-recaptcha";
import API_BASE_URI, { GOOGLE_RECAPTCHA_API } from "../../config/api.config";
import { isLoggedIn } from "./isLoggedIn";

// VITE_GEO_BASE_URI points only the maps calls elsewhere, e.g. at a local
// property service (http://localhost:3100) while the rest of the app uses dev.
export const GEO_BASE = `${import.meta.env.VITE_GEO_BASE_URI || API_BASE_URI}/api/property/geo`;

// Plain axios on purpose: the guarded instances cancel `x-auth-token` requests
// while signed out, and only /pass ever carries the token.
const http = axios.create({ baseURL: GEO_BASE, timeout: 20000 });

const authToken = () => {
  try {
    return JSON.parse(localStorage.getItem("token") || "null");
  } catch {
    return null;
  }
};

// ── invisible reCAPTCHA for signed-out visitors ─────────────────────────────
let captchaRef = null;
let captchaReady = null;

function ensureCaptcha() {
  if (captchaReady) return captchaReady;
  captchaReady = new Promise((resolve) => {
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    document.body.appendChild(host);
    createRoot(host).render(
      createElement(ReCAPTCHA, {
        sitekey: GOOGLE_RECAPTCHA_API,
        size: "invisible",
        ref: (r) => {
          if (r && !captchaRef) {
            captchaRef = r;
            resolve(r);
          }
        },
      })
    );
  });
  return captchaReady;
}

async function captchaToken() {
  // Local testing only: `vite dev` with VITE_GEO_DEV_NO_CAPTCHA set skips the
  // widget (headless browsers always get the image puzzle). Production builds
  // drop this branch, and only a NODE_ENV=local backend accepts the result.
  if (import.meta.env.DEV && import.meta.env.VITE_GEO_DEV_NO_CAPTCHA) return "dev";
  const r = await ensureCaptcha();
  const token = await r.executeAsync();
  r.reset();
  return token;
}

// ── the pass ────────────────────────────────────────────────────────────────
let current = null; // { pass, expiresAt, signedIn }
let inflight = null;

async function fetchPass() {
  const token = isLoggedIn() ? authToken() : null;
  const body = token ? {} : { captchaToken: await captchaToken() };
  const res = await http.post("/pass", body, token ? { headers: { "x-auth-token": token } } : {});
  const item = res.data?.data?.item;
  if (!item?.pass) throw new Error("No maps session");
  return { pass: item.pass, expiresAt: item.expiresAt, signedIn: Boolean(token) };
}

/**
 * A valid pass, fetched once and shared; renewed two minutes before expiry or
 * when the user signs in or out.
 */
export function ensureGeoPass() {
  const fresh =
    current &&
    current.expiresAt - Date.now() > 2 * 60 * 1000 &&
    current.signedIn === isLoggedIn();
  if (fresh) return Promise.resolve(current.pass);
  if (!inflight) {
    inflight = fetchPass()
      .then((p) => {
        current = p;
        return p.pass;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

async function get(path, params, { signal } = {}, retried = false) {
  const pass = await ensureGeoPass();
  try {
    const res = await http.get(path, { params, signal, headers: { "x-geo-pass": pass } });
    return res.data?.data?.item ?? null;
  } catch (err) {
    // An expired or rejected pass: drop it and try once with a new one.
    if (err?.response?.status === 401 && !retried) {
      current = null;
      return get(path, params, { signal }, true);
    }
    throw err;
  }
}

/** A Google autocomplete session id (one per search, ended by the pick). */
export function newSearchSession() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

// provider: "google" (screens on a Google map) | "osm" (screens on Esri maps,
// where Google's terms don't allow Google results).
export const geoApi = {
  autocomplete: (q, { provider = "google", session, lat, lng, signal } = {}) =>
    get("/autocomplete", { q, provider, session, lat, lng }, { signal }),
  place: (id, { session, signal } = {}) =>
    get(`/place/${encodeURIComponent(id)}`, { session }, { signal }),
  reverse: (lat, lng, { provider = "google", admin = false, signal } = {}) =>
    get("/reverse", { lat, lng, provider, admin: admin ? 1 : undefined }, { signal }),
  geocode: (q, { provider = "google", state, signal } = {}) =>
    get("/geocode", { q, provider, state }, { signal }),
  locate: (place, { provider = "google", signal } = {}) =>
    get("/locate", { ...place, provider }, { signal }),
  nearby: (lat, lng, type, { provider = "google", signal } = {}) =>
    get("/nearby", { lat, lng, type, provider }, { signal }),
  // Map credits, like the tiles, need no pass (see googleTileUrl).
  viewport: async (mapType, box, { signal } = {}) => {
    const res = await http.get(`/tiles/${mapType}/viewport`, { params: box, signal });
    return res.data?.data?.item ?? null;
  },
};

/**
 * One Google map tile. Tiles need no pass: a public page shows its map before
 * the visitor does anything, and the backend guards tiles by page origin, per-IP
 * limits and a daily ceiling instead.
 */
export const googleTileUrl = (mapType, z, x, y) => `${GEO_BASE}/tiles/${mapType}/${z}/${x}/${y}`;
